<template>
  <v-app>
    <v-app-bar v-if="authStore.isAuthenticated" app color="primary" dark>
      <v-app-bar-title :to="{ name: homeRoute }" class="cursor-pointer d-flex align-center">
        <img src="./assets/logo.png" alt="" width="32" height="32" class="mr-2" />
        Gigdriver
      </v-app-bar-title>

      <v-chip v-if="currentOrgName" class="mx-4" variant="outlined">
        {{ currentOrgName }}
      </v-chip>

      <v-spacer></v-spacer>

      <!-- Navigation. The org-scoped pages all bounce back to organization
           selection without a current org, so they're hidden until one is
           picked — My Offers works either way (self-service, not org-scoped). -->
      <template v-if="orgStore.currentOrg">
        <v-btn v-if="orgStore.isCoordinatorPlus" text :to="{ name: 'Dashboard' }" class="mr-2">Dashboard</v-btn>
        <v-btn v-if="orgStore.isCoordinatorPlus" text :to="{ name: 'GigsList' }" class="mr-2">Gigs</v-btn>
        <template v-if="orgStore.isManagerPlus">
          <v-btn text :to="{ name: 'ClientsList' }" class="mr-2">Clients</v-btn>
        </template>
        <v-btn v-if="orgStore.isCoordinatorPlus" text :to="{ name: 'PeopleList' }" class="mr-2">People</v-btn>
        <v-btn v-if="orgStore.isCoordinatorPlus" text :to="{ name: 'RateCardsList' }" class="mr-2">Rates</v-btn>
        <v-btn v-if="orgStore.isCoordinatorPlus" text :to="{ name: 'VenuesList' }" class="mr-2">Venues</v-btn>
        <v-btn v-if="orgStore.isCoordinatorPlus" text :to="{ name: 'TimesheetReview' }" class="mr-2">Timesheets</v-btn>
        <v-btn v-if="orgStore.isManagerPlus" text :to="{ name: 'PayrollList' }" class="mr-2">Payroll</v-btn>
      </template>
      <v-btn text :to="{ name: 'MyOffers' }" class="mr-2">My Offers</v-btn>
      <v-btn text :to="{ name: 'TimeClock' }" class="mr-2">Time Clock</v-btn>

      <!-- User Menu -->
      <v-menu>
        <template #activator="{ props }">
          <v-btn icon v-bind="props">
            <v-avatar v-if="authStore.currentUser?.photoURL" :image="authStore.currentUser.photoURL"></v-avatar>
            <v-icon v-else>mdi-account-circle</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item>
            <v-list-item-title>{{ authStore.currentUser?.displayName }}</v-list-item-title>
            <v-list-item-subtitle>{{ authStore.currentUser?.email }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item :to="{ name: 'MyAccount' }">
            <v-list-item-title>My Account</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="orgStore.currentOrg && orgStore.isOwnerOrAdmin" :to="{ name: 'Settings' }">
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="!orgStore.isStaffOnly" :to="{ name: 'OrganizationSelect' }">
            <v-list-item-title>{{ orgStore.currentOrg ? 'Switch Organization' : 'Select Organization' }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="handleLogout">
            <v-list-item-title>Sign Out</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <router-view v-slot="{ Component }">
        <component :is="Component" :key="$route.fullPath" />
      </router-view>
    </v-main>

    <v-footer app absolute class="justify-center text-caption text-medium-emphasis">
      <router-link :to="{ name: 'PrivacyPolicy' }" class="text-medium-emphasis mr-4">Privacy Policy</router-link>
      <router-link :to="{ name: 'TermsOfService' }" class="text-medium-emphasis">Terms of Service</router-link>
    </v-footer>
  </v-app>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useOrganizationStore } from './stores/organization';

const router = useRouter();
const authStore = useAuthStore();
const orgStore = useOrganizationStore();

const currentOrgName = computed(() => orgStore.currentOrg?.name);
// Staff-only users have no dashboard; My Offers is their home.
const homeRoute = computed(() => (orgStore.isStaffOnly ? 'MyOffers' : 'Dashboard'));

// Know the user's memberships/roles app-wide (not just on the org picker) so
// the nav can drop what a staff-only user doesn't need.
watch(
  () => authStore.currentUser?.uid,
  uid => {
    if (uid) orgStore.loadUserOrganizations(uid);
    else orgStore.clearOrganization();
  },
  { immediate: true },
);

async function handleLogout() {
  await authStore.logout();
  router.push('/login');
}
</script>

<style>
html {
  scroll-behavior: smooth;
}

body {
  font-family: 'Roboto', sans-serif;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
